import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  contributionGoalFormSchema,
  ContributionGoalFormValues,
} from "./schemas/contributionGoalForm.schema";
import {
  useCreateSaccoGoal,
  useUpdateSaccoGoal,
} from "@/hooks/sacco/useSaccoGoals.ts";
import {SaccoGoal} from "@/@types/sacco.models.ts";

interface ContributionGoalFormProps {
  membershipId: string;
  goal?: SaccoGoal;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function ContributionGoalForm({
  membershipId,
  goal,
  open,
  onOpenChange,
  onSuccess,
}: ContributionGoalFormProps) {
  const isEdit = !!goal;
  const createGoal = useCreateSaccoGoal(membershipId);
  const updateGoal = useUpdateSaccoGoal(membershipId);

  const form = useForm<ContributionGoalFormValues>({
    resolver: zodResolver(contributionGoalFormSchema),
    defaultValues: {
      goal_name: "",
      target_amount: "" as unknown as number,
      target_type: "deposits",
      target_date: "",
      description: "",
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        goal_name: goal?.goal_name ?? "",
        target_amount: goal?.target_amount != null ? Number(goal.target_amount) : ("" as unknown as number),
        target_type: (goal?.target_type as "deposits" | "share_capital") ?? "deposits",
        target_date: goal?.target_date ?? "",
        description: goal?.description ?? "",
      });
    }
  }, [open, goal, form]);

  const isPending = createGoal.isPending || updateGoal.isPending;

  async function onSubmit(values: ContributionGoalFormValues) {
    try {
      if (isEdit) {
        await updateGoal.mutateAsync({
          goalName: values.goal_name,
          isAchieved: false,
          targetAmount: values.target_amount,
          targetDate: values.target_date,
          targetType: values.target_type,
          id: goal.id
        });
        toast.success("Goal updated successfully");
      } else {
        await createGoal.mutateAsync({
          goalName: values.goal_name,
          targetAmount: values.target_amount,
          targetType: values.target_type,
          targetDate: values.target_date,
          description: values.description || null,
          membershipId: membershipId,
        });
        toast.success("Goal created successfully");
      }
      onOpenChange(false);
      onSuccess?.();
    } catch {
      toast.error(isEdit ? "Failed to update goal" : "Failed to create goal");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Goal" : "Add Contribution Goal"}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="goal_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Goal Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Save for emergency fund" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="target_amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Target Amount *</FormLabel>
                  <FormControl>
                    <Input type="text" inputMode="decimal" placeholder="0.00" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="target_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Target Type *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select target type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="deposits">Deposits</SelectItem>
                      <SelectItem value="share_capital">Share Capital</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="target_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Target Date *</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Optional description"
                      className="resize-none"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Saving..." : isEdit ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
